import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Badge, Spinner, Alert,
  Modal, Form, Tab, Tabs,
} from 'react-bootstrap';
import { colors } from '../../brand-colors';

const VIDEO_FORMATS = {
  youtube_landscape: 'YouTube (16:9)',
  tiktok_portrait: 'TikTok / Reels (9:16)',
  youtube_short: 'YouTube Short (9:16)',
  square: 'Square (1:1)',
};

const SCENE_TYPE_COLORS = {
  hook: '#D03737',
  teaching: '#1E3A5F',
  proof: '#816532',
  bridge: '#7f7f7f',
  cta: '#A30202',
  transition: '#bfbfbf',
};

const SCENE_TYPES = ['hook', 'teaching', 'proof', 'bridge', 'cta', 'transition'];
const DELIVERY_MODES = ['talking_head', 'voiceover_broll', 'voiceover_screenrecord'];

const DELIVERY_LABELS = {
  talking_head: 'On Camera',
  voiceover_broll: 'Voiceover + B-Roll',
  voiceover_screenrecord: 'Voiceover + Screen',
};

function formatDuration(totalSeconds) {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

// ============================================================================
// SCENE EDITOR MODAL
// ============================================================================

function SceneEditorModal({ show, onHide, draft, onSave, onApprove, onReject }) {
  const [scenes, setScenes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (draft?.video_scenes) {
      setScenes(JSON.parse(JSON.stringify(draft.video_scenes)));
    }
  }, [draft]);

  const totalDuration = scenes.reduce((sum, s) => sum + (s.duration_seconds || 0), 0);
  const talkingHeadDuration = scenes
    .filter(s => s.delivery === 'talking_head')
    .reduce((sum, s) => sum + (s.duration_seconds || 0), 0);
  const voiceoverDuration = totalDuration - talkingHeadDuration;

  const updateScene = (index, field, value) => {
    const updated = [...scenes];
    updated[index] = { ...updated[index], [field]: value };
    setScenes(updated);
  };

  const removeScene = (index) => {
    const updated = scenes.filter((_, i) => i !== index);
    updated.forEach((s, i) => { s.scene_number = i + 1; });
    setScenes(updated);
  };

  const addScene = () => {
    setScenes([...scenes, {
      scene_number: scenes.length + 1,
      scene_type: 'teaching',
      delivery: 'voiceover_broll',
      script_text: '',
      duration_seconds: 30,
      visual_cue: '',
      background_suggestion: null,
    }]);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(draft.id, scenes);
    setSaving(false);
  };

  const handleApprove = async () => {
    setApproving(true);
    await onApprove(draft.id);
    setApproving(false);
  };

  if (!draft) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton style={{
        backgroundColor: colors.greyDarkest, color: colors.goldLight,
        fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
        letterSpacing: '0.1em',
      }}>
        <Modal.Title style={{ fontSize: '1rem' }}>
          {draft.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Duration summary */}
        <div className="d-flex gap-3 mb-3 p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
          <Badge bg="dark" className="px-3 py-2">
            {formatDuration(totalDuration)} total
          </Badge>
          <Badge style={{ backgroundColor: SCENE_TYPE_COLORS.hook }} className="px-3 py-2">
            {talkingHeadDuration}s on camera
          </Badge>
          <Badge style={{ backgroundColor: SCENE_TYPE_COLORS.teaching }} className="px-3 py-2">
            {formatDuration(voiceoverDuration)} voiceover
          </Badge>
          {talkingHeadDuration > 15 && (
            <Badge bg="warning" text="dark" className="px-3 py-2">
              Talking head exceeds 13s target
            </Badge>
          )}
        </div>

        {/* Scene cards */}
        {scenes.map((scene, index) => (
          <Card key={index} className="mb-3" style={{ border: `2px solid ${SCENE_TYPE_COLORS[scene.scene_type] || '#ccc'}` }}>
            <Card.Header className="d-flex align-items-center gap-2 py-2" style={{ backgroundColor: '#f8f9fa' }}>
              <Badge style={{ backgroundColor: SCENE_TYPE_COLORS[scene.scene_type] || '#666' }}>
                {scene.scene_type?.toUpperCase()}
              </Badge>
              <Form.Select
                size="sm"
                value={scene.scene_type}
                onChange={(e) => updateScene(index, 'scene_type', e.target.value)}
                style={{ width: '130px' }}
              >
                {SCENE_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                value={scene.delivery}
                onChange={(e) => updateScene(index, 'delivery', e.target.value)}
                style={{ width: '200px' }}
              >
                {DELIVERY_MODES.map(d => (
                  <option key={d} value={d}>{DELIVERY_LABELS[d]}</option>
                ))}
              </Form.Select>
              <div className="d-flex align-items-center gap-1 ms-auto">
                <Form.Control
                  type="number"
                  size="sm"
                  min={1}
                  max={300}
                  value={scene.duration_seconds}
                  onChange={(e) => updateScene(index, 'duration_seconds', parseInt(e.target.value) || 1)}
                  style={{ width: '70px' }}
                />
                <span className="text-muted small">sec</span>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeScene(index)}
                title="Remove scene"
              >
                &times;
              </Button>
            </Card.Header>
            <Card.Body className="py-2">
              <Form.Control
                as="textarea"
                rows={3}
                value={scene.script_text}
                onChange={(e) => updateScene(index, 'script_text', e.target.value)}
                placeholder="Script text (what to say)..."
                className="mb-2"
              />
              <Form.Control
                size="sm"
                value={scene.visual_cue || ''}
                onChange={(e) => updateScene(index, 'visual_cue', e.target.value)}
                placeholder="Visual cue (what the viewer sees)..."
                style={{ color: colors.greyMid }}
              />
            </Card.Body>
          </Card>
        ))}

        <Button variant="outline-dark" size="sm" onClick={addScene} className="mb-3">
          + Add Scene
        </Button>
      </Modal.Body>
      <Modal.Footer>
        {draft.status === 'draft' && (
          <>
            <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
            <Button
              variant="dark"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><Spinner size="sm" className="me-1" /> Saving...</> : 'Save Changes'}
            </Button>
            <Button
              style={{ background: 'linear-gradient(90deg, #ddb75e 0%, #816532 100%)', border: 'none', color: '#fff' }}
              onClick={handleApprove}
              disabled={approving}
            >
              {approving ? <><Spinner size="sm" className="me-1" /> Approving...</> : 'Approve Script'}
            </Button>
          </>
        )}
        {draft.status === 'approved' && (
          <Badge bg="success" className="px-3 py-2">Approved</Badge>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function VideoScripts() {
  const [activeTab, setActiveTab] = useState('from-blog');

  // From Blog tab state
  const [blogPosts, setBlogPosts] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [videoFormat, setVideoFormat] = useState('youtube_landscape');
  const [generating, setGenerating] = useState(false);
  const [generatingClips, setGeneratingClips] = useState(false);

  // Standalone tab state
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [standaloneFormat, setStandaloneFormat] = useState('youtube_landscape');
  const [customTopic, setCustomTopic] = useState('');
  const [customThesis, setCustomThesis] = useState('');
  const [customOutcome, setCustomOutcome] = useState('');
  const [generatingTopics, setGeneratingTopics] = useState(false);
  const [generatingStandalone, setGeneratingStandalone] = useState(false);

  // Scripts list state
  const [scripts, setScripts] = useState([]);
  const [loadingScripts, setLoadingScripts] = useState(true);

  // Shared state
  const [error, setError] = useState('');
  const [loadingBlogs, setLoadingBlogs] = useState(true);

  // Modal state
  const [editingDraft, setEditingDraft] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  // Load approved blog posts
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch('/api/content/drafts?status=approved');
        console.log('[VideoScripts] Blog fetch status:', res.status);
        if (res.ok) {
          const data = await res.json();
          console.log('[VideoScripts] Blog posts loaded:', data.drafts?.length);
          setBlogPosts(data.drafts || []);
        } else {
          console.error('[VideoScripts] Blog fetch failed:', res.status, await res.text());
        }
      } catch (err) {
        console.error('Failed to load blog posts:', err);
      } finally {
        setLoadingBlogs(false);
      }
    };
    fetchBlogs();
  }, []);

  // Load video scripts
  const fetchScripts = useCallback(async () => {
    setLoadingScripts(true);
    try {
      const res = await fetch('/api/content/video-scripts');
      if (res.ok) {
        const data = await res.json();
        setScripts(data.drafts || []);
      }
    } catch (err) {
      console.error('Failed to load scripts:', err);
    } finally {
      setLoadingScripts(false);
    }
  }, []);

  useEffect(() => { fetchScripts(); }, [fetchScripts]);

  // ---- Actions ----

  const handleGenerateFromBlog = async () => {
    if (!selectedBlog) return;
    setGenerating(true);
    setError('');
    try {
      const res = await fetch('/api/content/video-scripts/from-blog', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ blog_draft_id: selectedBlog.id, video_format: videoFormat }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setEditingDraft(data.draft);
      setShowEditor(true);
      fetchScripts();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateClips = async () => {
    if (!selectedBlog) return;
    setGeneratingClips(true);
    setError('');
    try {
      const res = await fetch('/api/content/video-scripts/clips-from-blog', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ blog_draft_id: selectedBlog.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      const clips = data.drafts || [];
      if (clips.length > 0) {
        setEditingDraft(clips[0]);
        setShowEditor(true);
      }
      fetchScripts();
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingClips(false);
    }
  };

  const handleGenerateTopics = async () => {
    setGeneratingTopics(true);
    setError('');
    try {
      const res = await fetch('/api/content/video-scripts/generate-topics', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ count: 5, topic_mode: 'emerging' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate topics');
      setTopics(data.topics || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingTopics(false);
    }
  };

  const handleGenerateStandalone = async () => {
    const topic = selectedTopic?.topic || customTopic;
    if (!topic) { setError('Select or enter a topic'); return; }
    setGeneratingStandalone(true);
    setError('');
    try {
      const res = await fetch('/api/content/video-scripts/standalone', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          topic,
          thesis: selectedTopic?.thesis || customThesis,
          outcome: selectedTopic?.outcome || customOutcome,
          video_format: standaloneFormat,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setEditingDraft(data.draft);
      setShowEditor(true);
      fetchScripts();
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingStandalone(false);
    }
  };

  const handleSaveScenes = async (draftId, scenes) => {
    try {
      const res = await fetch(`/api/content/video-scripts/${draftId}/scenes`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ scenes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setEditingDraft(data.draft);
      fetchScripts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApprove = async (draftId) => {
    try {
      const res = await fetch(`/api/content/drafts/${draftId}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Approval failed');
      setShowEditor(false);
      setEditingDraft(null);
      fetchScripts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (draftId) => {
    try {
      const res = await fetch(`/api/content/drafts/${draftId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Delete failed');
      }
      setShowEditor(false);
      setEditingDraft(null);
      fetchScripts();
    } catch (err) {
      setError(err.message);
    }
  };

  // ---- Render helpers ----

  const draftScripts = scripts.filter(s => s.status === 'draft');
  const approvedScripts = scripts.filter(s => s.status === 'approved');

  return (
    <Container className="py-4">
      <div className="mb-4">
        <h1 style={{
          fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
          letterSpacing: '0.15em',
          color: colors.greyDarkest,
        }}>
          Video Scripts
        </h1>
        <p className="text-muted">
          Generate video scripts from your blog posts or standalone topics.
          Scripts are reviewed and edited before video production (Phase 2).
        </p>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Tabs
        activeKey={activeTab}
        onSelect={setActiveTab}
        className="mb-4"
      >
        {/* ============================================================ */}
        {/* TAB 1: FROM BLOG POST */}
        {/* ============================================================ */}
        <Tab eventKey="from-blog" title="From Blog Post">
          <Row>
            <Col md={8}>
              <h5 className="mb-3">Select an approved post</h5>
              {loadingBlogs ? (
                <div className="text-center py-4"><Spinner /></div>
              ) : blogPosts.length === 0 ? (
                <Alert variant="info">
                  No approved posts yet. Generate and approve content in Long Form or Short Form Posts first.
                </Alert>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {blogPosts.map(post => (
                    <Card
                      key={post.id}
                      className="mb-2"
                      style={{
                        cursor: 'pointer',
                        border: selectedBlog?.id === post.id
                          ? `2px solid ${colors.goldMid}`
                          : `1px solid ${colors.greyLight}`,
                        backgroundColor: selectedBlog?.id === post.id ? '#fffdf5' : '#fff',
                      }}
                      onClick={() => setSelectedBlog(post)}
                    >
                      <Card.Body className="py-2 px-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>{post.title}</strong>
                            <div className="text-muted small">
                              {post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}
                            </div>
                          </div>
                          <div className="d-flex gap-1 align-items-center">
                            <Badge bg={post.content_type === 'long_form' ? 'dark' : 'secondary'} style={{ fontSize: '0.65rem' }}>
                              {post.content_type === 'long_form' ? 'Blog' : 'Short'}
                            </Badge>
                            {selectedBlog?.id === post.id && (
                              <Badge style={{ backgroundColor: colors.goldMid }}>Selected</Badge>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Col>
            <Col md={4}>
              <Card style={{ border: `1px solid ${colors.greyLight}` }}>
                <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                  <strong>Video Settings</strong>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Format</Form.Label>
                    <Form.Select value={videoFormat} onChange={(e) => setVideoFormat(e.target.value)}>
                      {Object.entries(VIDEO_FORMATS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Button
                    className="w-100 mb-2"
                    style={{ background: 'linear-gradient(90deg, #D03737 0%, #A30202 100%)', border: 'none' }}
                    onClick={handleGenerateFromBlog}
                    disabled={!selectedBlog || generating}
                  >
                    {generating ? <><Spinner size="sm" className="me-1" /> Generating Script...</> : 'Generate Script'}
                  </Button>

                  <Button
                    variant="outline-dark"
                    className="w-100"
                    onClick={handleGenerateClips}
                    disabled={!selectedBlog || generatingClips}
                  >
                    {generatingClips ? <><Spinner size="sm" className="me-1" /> Generating Clips...</> : 'Generate Short Clips'}
                  </Button>

                  <div className="text-muted small mt-2">
                    Script generates a 3-7 min video outline. Clips create 2-4 standalone 60-90 sec shorts.
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* ============================================================ */}
        {/* TAB 2: STANDALONE SCRIPT */}
        {/* ============================================================ */}
        <Tab eventKey="standalone" title="Standalone Script">
          <Row>
            <Col md={8}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Video Topics</h5>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={handleGenerateTopics}
                  disabled={generatingTopics}
                >
                  {generatingTopics ? <><Spinner size="sm" className="me-1" /> Generating...</> : 'Generate Topic Ideas'}
                </Button>
              </div>

              {topics.length > 0 && (
                <div className="mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {topics.map((t, i) => (
                    <Card
                      key={i}
                      className="mb-2"
                      style={{
                        cursor: 'pointer',
                        border: selectedTopic === t ? `2px solid ${colors.goldMid}` : `1px solid ${colors.greyLight}`,
                        backgroundColor: selectedTopic === t ? '#fffdf5' : '#fff',
                      }}
                      onClick={() => { setSelectedTopic(t); setCustomTopic(''); }}
                    >
                      <Card.Body className="py-2 px-3">
                        <strong>{t.topic}</strong>
                        {t.thesis && <div className="text-muted small">{t.thesis}</div>}
                        {t.hook_idea && <div className="small mt-1" style={{ color: colors.goldDark }}>Hook: {t.hook_idea}</div>}
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}

              <h6 className="mt-3">Or enter your own topic</h6>
              <Form.Control
                className="mb-2"
                placeholder="Topic / title"
                value={customTopic}
                onChange={(e) => { setCustomTopic(e.target.value); setSelectedTopic(null); }}
              />
              <Form.Control
                className="mb-2"
                placeholder="Thesis / angle (optional)"
                value={customThesis}
                onChange={(e) => setCustomThesis(e.target.value)}
              />
              <Form.Control
                placeholder="Desired outcome (optional)"
                value={customOutcome}
                onChange={(e) => setCustomOutcome(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Card style={{ border: `1px solid ${colors.greyLight}` }}>
                <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                  <strong>Video Settings</strong>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Format</Form.Label>
                    <Form.Select value={standaloneFormat} onChange={(e) => setStandaloneFormat(e.target.value)}>
                      {Object.entries(VIDEO_FORMATS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Button
                    className="w-100"
                    style={{ background: 'linear-gradient(90deg, #D03737 0%, #A30202 100%)', border: 'none' }}
                    onClick={handleGenerateStandalone}
                    disabled={(!selectedTopic && !customTopic) || generatingStandalone}
                  >
                    {generatingStandalone ? <><Spinner size="sm" className="me-1" /> Generating...</> : 'Generate Script'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* ============================================================ */}
        {/* TAB 3: MY SCRIPTS */}
        {/* ============================================================ */}
        <Tab eventKey="my-scripts" title={`My Scripts${scripts.length ? ` (${scripts.length})` : ''}`}>
          {loadingScripts ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : scripts.length === 0 ? (
            <Alert variant="info">
              No video scripts yet. Generate one from a blog post or a standalone topic.
            </Alert>
          ) : (
            <>
              {/* Drafts section */}
              {draftScripts.length > 0 && (
                <>
                  <h5 className="mb-3">Drafts</h5>
                  <Row>
                    {draftScripts.map(script => (
                      <Col md={6} key={script.id} className="mb-3">
                        <ScriptCard
                          script={script}
                          onClick={() => { setEditingDraft(script); setShowEditor(true); }}
                        />
                      </Col>
                    ))}
                  </Row>
                </>
              )}

              {/* Approved section */}
              {approvedScripts.length > 0 && (
                <>
                  <h5 className="mb-3 mt-4">Approved</h5>
                  <Row>
                    {approvedScripts.map(script => (
                      <Col md={6} key={script.id} className="mb-3">
                        <ScriptCard
                          script={script}
                          onClick={() => { setEditingDraft(script); setShowEditor(true); }}
                        />
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </>
          )}
        </Tab>
      </Tabs>

      {/* Scene Editor Modal */}
      <SceneEditorModal
        show={showEditor}
        onHide={() => { setShowEditor(false); setEditingDraft(null); }}
        draft={editingDraft}
        onSave={handleSaveScenes}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </Container>
  );
}

// ============================================================================
// SCRIPT CARD COMPONENT
// ============================================================================

function ScriptCard({ script, onClick }) {
  const sceneCount = script.video_scenes?.length || 0;
  const isClip = script.content_type === 'video_clip';

  return (
    <Card
      className="h-100 shadow-sm"
      style={{
        border: `1px solid ${colors.greyLight}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.goldMid;
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = colors.greyLight;
        e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}
        className="d-flex justify-content-between align-items-center"
      >
        <strong style={{ fontSize: '0.9rem' }}>{script.title}</strong>
        <div className="d-flex gap-1">
          <Badge bg={isClip ? 'info' : 'secondary'} style={{ fontSize: '0.7rem' }}>
            {isClip ? 'Clip' : 'Script'}
          </Badge>
          <Badge bg={script.status === 'approved' ? 'success' : 'warning'} style={{ fontSize: '0.7rem' }}>
            {script.status}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between text-muted small mb-1">
          <span>{sceneCount} scenes</span>
          <span>{script.video_total_duration ? formatDuration(script.video_total_duration) : '—'}</span>
        </div>
        <div className="d-flex gap-1 flex-wrap mb-1">
          {(script.video_scenes || []).slice(0, 8).map((scene, i) => (
            <span
              key={i}
              style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: SCENE_TYPE_COLORS[scene.scene_type] || '#ccc',
                display: 'inline-block',
              }}
              title={scene.scene_type}
            />
          ))}
        </div>
        <div className="text-muted small">
          {VIDEO_FORMATS[script.video_format] || script.video_format}
          {script.talking_head_seconds != null && ` · ${script.talking_head_seconds}s on camera`}
        </div>
        {script.created_at && (
          <div className="text-muted small">{new Date(script.created_at).toLocaleDateString()}</div>
        )}
      </Card.Body>
    </Card>
  );
}

export default VideoScripts;
